import type {AccessLogRecord} from '@/entities/log/types';
import type {AttendanceDayRow, CompanyAttendanceReport} from '@/entities/attendance/types';
import {isoDate} from '@/entities/attendance/period';

/**
 * Числа для «Главной», посчитанные из того, что гейтвей уже отдаёт.
 *
 * Новых маршрутов у дашборда нет и не нужно: проходы за неделю — это журнал за
 * неделю, «кто внутри» — сегодняшний журнал, свёрнутый до человека, «опоздавшие» —
 * отчёт явки за сегодня со строками дней. Здесь только арифметика над этим; ходить в
 * сеть — дело экрана.
 */

/** Один столбик тренда: день и два числа. */
export type DayPasses = {
  date: string;
  granted: number;
  denied: number;
};

/** Локальная дата события — той же формой, что и `isoDate`. */
function dateOf(millis: number): string {
  return isoDate(new Date(millis));
}

/** Последние `count` дней по возрастанию, последний — `today`. */
export function lastDays(today: string, count: number): string[] {
  const end = new Date(`${today}T00:00:00`);
  const days: string[] = [];
  for (let i = count - 1; i >= 0; i -= 1) {
    const d = new Date(end);
    d.setDate(end.getDate() - i);
    days.push(isoDate(d));
  }
  return days;
}

/**
 * Проходы по дням. Дни без событий тоже в ответе, с нулями: столбик тренда за
 * выходной обязан быть пустым столбиком, а не отсутствующим.
 */
export function passesByDay(events: readonly AccessLogRecord[], days: readonly string[]): DayPasses[] {
  const index = new Map<string, DayPasses>();
  for (const date of days) {
    index.set(date, {date, granted: 0, denied: 0});
  }
  for (const e of events) {
    const bucket = index.get(dateOf(e.created_at_millis));
    if (!bucket) {
      continue;
    }
    if (e.granted) {
      bucket.granted += 1;
    } else {
      bucket.denied += 1;
    }
  }
  return days.map(date => index.get(date) as DayPasses);
}

/** Проходы за один день по часам: 24 корзины, 0..23. */
export function passesByHour(
  events: readonly AccessLogRecord[],
  date: string,
): {granted: number[]; denied: number[]} {
  const granted = new Array<number>(24).fill(0);
  const denied = new Array<number>(24).fill(0);
  for (const e of events) {
    if (dateOf(e.created_at_millis) !== date) {
      continue;
    }
    const hour = new Date(e.created_at_millis).getHours();
    if (e.granted) {
      granted[hour] += 1;
    } else {
      denied[hour] += 1;
    }
  }
  return {granted, denied};
}

/** Кто опоздал сегодня — по строкам дней отчёта, с минутами, от самых поздних. */
export type LateRow = {
  recordId: string;
  name: string;
  minutes: number;
  arrivalMillis: number | null;
};

export function lateToday(report: CompanyAttendanceReport | undefined): LateRow[] {
  if (!report) {
    return [];
  }
  const rows: LateRow[] = [];
  for (const day of report.days as AttendanceDayRow[]) {
    const late = day.flags.includes('late') || (day.arrival_delta_minutes ?? 0) > 0;
    if (!day.scheduled || !late) {
      continue;
    }
    rows.push({
      recordId: day.record_id,
      name: day.full_name,
      minutes: Math.max(0, Math.round(day.arrival_delta_minutes ?? 0)),
      arrivalMillis: day.arrival_time_millis,
    });
  }
  return rows.sort((a, b) => b.minutes - a.minutes);
}

/** Кто по графику должен был быть, но ни разу не отметился. */
export function absentToday(report: CompanyAttendanceReport | undefined): string[] {
  if (!report) {
    return [];
  }
  return (report.days as AttendanceDayRow[])
    .filter(day => day.scheduled && day.flags.includes('absent'))
    .map(day => day.full_name)
    .sort((a, b) => a.localeCompare(b));
}
